import React from "react";
import * as THREE from "three";
import { HeroThree } from "./hero-three.jsx";
import { S, openConsult } from "../site.js";

const {
  useEffect: useLiquidGlassEffect,
  useRef: useLiquidGlassRef,
} = React;

const startConsultation = openConsult({ source: "hero" });

const LIQUID_GLASS_REVEAL_MS = 1180;

const LIQUID_GLASS_INNER_VERTEX = `
  varying vec3 vNormal;
  varying vec3 vViewPosition;
  varying vec3 vObjectPosition;
  void main() {
    vec4 worldPosition = modelMatrix * vec4(position, 1.0);
    vNormal = normalize(mat3(modelMatrix) * normal);
    vViewPosition = normalize(cameraPosition - worldPosition.xyz);
    vObjectPosition = position;
    gl_Position = projectionMatrix * viewMatrix * worldPosition;
  }
`;

const LIQUID_GLASS_INNER_FRAGMENT = `
  precision highp float;
  uniform float uReveal;
  varying vec3 vNormal;
  varying vec3 vViewPosition;
  varying vec3 vObjectPosition;

  void main() {
    vec3 n = normalize(vNormal);
    vec3 v = normalize(vViewPosition);
    vec3 p = vObjectPosition;
    float cyanField = smoothstep(0.58, -1.38, p.x) * smoothstep(-0.42, 0.88, p.y);
    float limeField = smoothstep(0.22, -1.12, p.y) * (1.0 - smoothstep(0.28, 1.30, abs(p.x)));
    float coralField = smoothstep(-0.12, 1.18, p.x) * smoothstep(-0.84, 0.70, p.y + p.x * 0.10);
    vec3 colour = vec3(0.16, 0.55, 0.86) * cyanField;
    colour += vec3(0.28, 0.87, 0.92) * cyanField * 0.82;
    colour += vec3(0.78, 0.90, 0.24) * limeField;
    colour += vec3(0.97, 0.40, 0.29) * coralField;
    colour += vec3(0.70, 0.16, 0.46) * coralField * smoothstep(0.42, 1.34, p.x);
    float weight = max(.17, cyanField + limeField + coralField);
    colour /= weight;
    float fresnel = pow(1.0 - max(dot(n, v), 0.0), 2.2);
    float highlight = pow(max(dot(reflect(normalize(vec3(-0.42, 0.72, 0.58)), n), v), 0.0), 28.0);
    colour = mix(colour * 0.94, colour * 1.28 + vec3(1.0, .98, .93) * highlight, fresnel * .58 + .15);
    gl_FragColor = vec4(colour, (.12 + fresnel * .20) * (.72 + uReveal * .28));
  }
`;

function createLiquidGlassLoop() {
  const points = [];
  for (let index = 0; index < 16; index += 1) {
    const t = (index / 16) * Math.PI * 2;
    const radius = 1.43 + Math.cos(t * 3) * 0.29;
    points.push(new THREE.Vector3(
      radius * Math.cos(t) + Math.cos(t * 2) * 0.14,
      radius * Math.sin(t) * 0.66 - Math.sin(t * 2) * 0.17,
      Math.sin(t * 2) * 0.28 + Math.cos(t * 3) * 0.10,
    ));
  }
  return new THREE.CatmullRomCurve3(points, true, "centripetal", 0.48);
}

function createLiquidGlassInnerMaterial() {
  return new THREE.ShaderMaterial({
    vertexShader: LIQUID_GLASS_INNER_VERTEX,
    fragmentShader: LIQUID_GLASS_INNER_FRAGMENT,
    uniforms: {
      uReveal: { value: 1 },
    },
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
}

function createLiquidGlassEnvironment(renderer) {
  const source = document.createElement("canvas");
  source.width = 512;
  source.height = 256;
  const context = source.getContext("2d");
  const sky = context.createLinearGradient(0, 0, source.width, source.height);
  sky.addColorStop(0, "#b9eff2");
  sky.addColorStop(0.28, "#f4faf9");
  sky.addColorStop(0.56, "#f6e9a5");
  sky.addColorStop(0.82, "#f3b5a7");
  sky.addColorStop(1, "#f7f4ed");
  context.fillStyle = sky;
  context.fillRect(0, 0, source.width, source.height);
  const highlight = context.createRadialGradient(175, 44, 8, 175, 44, 165);
  highlight.addColorStop(0, "rgba(255,255,255,.98)");
  highlight.addColorStop(1, "rgba(255,255,255,0)");
  context.fillStyle = highlight;
  context.fillRect(0, 0, source.width, source.height);
  const texture = new THREE.CanvasTexture(source);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  const generator = new THREE.PMREMGenerator(renderer);
  const environment = generator.fromEquirectangular(texture).texture;
  return { environment, generator, texture };
}

function HomeLiquidGlassHero() {
  const heroRef = useLiquidGlassRef(null);
  const visualRef = useLiquidGlassRef(null);
  const canvasRef = useLiquidGlassRef(null);
  const fallbackRef = useLiquidGlassRef(null);

  useLiquidGlassEffect(() => {
    const hero = heroRef.current;
    const visual = visualRef.current;
    const canvas = canvasRef.current;
    const fallback = fallbackRef.current;
    if (!hero || !visual || !canvas || !fallback) return undefined;

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const compactQuery = window.matchMedia("(max-width: 720px)");
    let renderer;
    let scene;
    let camera;
    let shellMaterial;
    let innerMaterial;
    let rafId = null;
    let revealElapsed = motionQuery.matches ? LIQUID_GLASS_REVEAL_MS : 0;
    let lastFrame = null;
    let intersecting = false;
    let disposed = false;
    let pointerFrame = null;
    const pointer = { x: 0, y: 0, targetX: 0, targetY: 0 };

    const setFallback = () => {
      fallback.hidden = false;
      canvas.style.display = "none";
      hero.dataset.liquidGlass = "fallback";
    };

    const setFixedUiSafeState = () => {
      const active = intersecting && window.innerWidth <= 1020;
      document.body.classList.toggle("home-liquid-glass-safe-active", active);
    };

    try {
      if (!canvas.getContext("webgl2") && !canvas.getContext("webgl")) {
        setFallback();
        return undefined;
      }

      renderer = new THREE.WebGLRenderer({
        canvas,
        alpha: true,
        antialias: !compactQuery.matches,
        powerPreference: "high-performance",
      });
      renderer.setClearColor(0x000000, 0);
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.1;
      if ("outputColorSpace" in renderer) renderer.outputColorSpace = THREE.SRGBColorSpace;
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(30, 1, 0.1, 100);
      camera.position.set(0, 0, 7.2);

      const environment = createLiquidGlassEnvironment(renderer);
      scene.environment = environment.environment;
      scene.add(new THREE.HemisphereLight(0xf8ffff, 0xf0caa8, 1.5));
      const keyLight = new THREE.DirectionalLight(0xffffff, 2.7);
      keyLight.position.set(-3.2, 4.5, 5.4);
      scene.add(keyLight);
      const cyanLight = new THREE.PointLight(0x71e7ef, 2.2, 8);
      cyanLight.position.set(-3.4, 1.2, 2.8);
      scene.add(cyanLight);
      const coralLight = new THREE.PointLight(0xff9c87, 1.85, 7);
      coralLight.position.set(3.2, -0.4, 2.2);
      scene.add(coralLight);

      const root = new THREE.Group();
      root.rotation.set(-0.12, 0.14, -0.08);
      scene.add(root);

      const curve = createLiquidGlassLoop();
      const shellGeometry = new THREE.TubeGeometry(curve, 340, 0.215, 28, true);
      const innerGeometry = new THREE.TubeGeometry(curve, 340, 0.115, 22, true);
      const rimGeometry = new THREE.TubeGeometry(curve, 340, 0.229, 20, true);
      shellMaterial = new THREE.MeshPhysicalMaterial({
        color: 0xf4fbff,
        roughness: 0.035,
        metalness: 0,
        transmission: 0.98,
        thickness: 0.72,
        ior: 1.33,
        transparent: true,
        opacity: 0.32,
        clearcoat: 1,
        clearcoatRoughness: 0.025,
        attenuationColor: new THREE.Color(0xcceef0),
        attenuationDistance: 3.8,
        depthWrite: false,
        side: THREE.FrontSide,
      });
      innerMaterial = createLiquidGlassInnerMaterial();
      const rimMaterial = new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        roughness: 0.11,
        transmission: 0.74,
        thickness: 0.45,
        ior: 1.31,
        transparent: true,
        opacity: 0.07,
        depthWrite: false,
        side: THREE.BackSide,
      });
      const shell = new THREE.Mesh(shellGeometry, shellMaterial);
      const inner = new THREE.Mesh(innerGeometry, innerMaterial);
      const rim = new THREE.Mesh(rimGeometry, rimMaterial);
      root.scale.set(1.26, 1.16, 1);
      root.add(inner, shell, rim);

      const shadowMaterial = new THREE.MeshBasicMaterial({ color: 0x65707c, transparent: true, opacity: 0.055, depthWrite: false });
      const shadow = new THREE.Mesh(new THREE.CircleGeometry(1.48, 64), shadowMaterial);
      shadow.scale.set(1.18, 0.28, 1);
      shadow.position.set(0.06, -1.56, -1.6);
      scene.add(shadow);

      const causticMaterials = [
        new THREE.MeshBasicMaterial({ color: 0x66dce4, transparent: true, opacity: 0.065, depthWrite: false, blending: THREE.AdditiveBlending }),
        new THREE.MeshBasicMaterial({ color: 0xd7e552, transparent: true, opacity: 0.06, depthWrite: false, blending: THREE.AdditiveBlending }),
        new THREE.MeshBasicMaterial({ color: 0xef907d, transparent: true, opacity: 0.052, depthWrite: false, blending: THREE.AdditiveBlending }),
      ];
      const caustic = new THREE.Group();
      const cyanCaustic = new THREE.Mesh(new THREE.RingGeometry(0.34, 0.9, 48), causticMaterials[0]);
      cyanCaustic.scale.set(1.25, 0.23, 1);
      cyanCaustic.position.set(-0.86, -1.55, -1.56);
      const limeCaustic = new THREE.Mesh(new THREE.CircleGeometry(0.55, 48), causticMaterials[1]);
      limeCaustic.scale.set(1.13, 0.18, 1);
      limeCaustic.position.set(-0.02, -1.57, -1.57);
      const coralCaustic = new THREE.Mesh(new THREE.RingGeometry(0.14, 0.58, 48), causticMaterials[2]);
      coralCaustic.scale.set(1.15, 0.22, 1);
      coralCaustic.position.set(0.93, -1.56, -1.58);
      caustic.add(cyanCaustic, limeCaustic, coralCaustic);
      scene.add(caustic);

      const resize = () => {
        const bounds = visual.getBoundingClientRect();
        const width = Math.max(1, Math.round(bounds.width));
        const height = Math.max(1, Math.round(bounds.height));
        const dpr = Math.min(window.devicePixelRatio || 1, compactQuery.matches ? 1.15 : 1.6);
        renderer.setPixelRatio(dpr);
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        camera.position.z = compactQuery.matches ? 7.9 : 7.2;
        camera.updateProjectionMatrix();
        render();
      };

      const render = () => {
        if (!renderer || !scene || !camera) return;
        root.rotation.y = 0.14 + pointer.x * 0.045;
        root.rotation.x = -0.12 - pointer.y * 0.032;
        renderer.render(scene, camera);
      };

      const canAnimate = () => !disposed
        && !motionQuery.matches
        && intersecting
        && document.visibilityState === "visible"
        && revealElapsed < LIQUID_GLASS_REVEAL_MS;

      const stop = () => {
        if (rafId !== null) {
          window.cancelAnimationFrame(rafId);
          rafId = null;
        }
        lastFrame = null;
      };

      const frame = (now) => {
        if (!canAnimate()) {
          stop();
          return;
        }
        if (lastFrame !== null) revealElapsed += now - lastFrame;
        lastFrame = now;
        const progress = Math.min(1, revealElapsed / LIQUID_GLASS_REVEAL_MS);
        shellMaterial.opacity = 0.12 + progress * 0.20;
        innerMaterial.uniforms.uReveal.value = progress;
        rimMaterial.opacity = 0.02 + progress * 0.05;
        root.scale.setScalar(0.93 + progress * 0.07);
        root.position.y = (1 - progress) * 0.13;
        shadowMaterial.opacity = 0.02 + progress * 0.035;
        causticMaterials.forEach((material, index) => { material.opacity = (0.022 + index * 0.003) + progress * 0.038; });
        render();
        if (progress < 1) rafId = window.requestAnimationFrame(frame);
        else stop();
      };

      const playIfAllowed = () => {
        if (motionQuery.matches) {
          revealElapsed = LIQUID_GLASS_REVEAL_MS;
          shellMaterial.opacity = 0.32;
          innerMaterial.uniforms.uReveal.value = 1;
          rimMaterial.opacity = 0.07;
          root.scale.setScalar(1);
          root.position.y = 0;
          shadowMaterial.opacity = 0.055;
          causticMaterials.forEach((material, index) => { material.opacity = 0.06 + index * 0.002; });
          render();
          stop();
          return;
        }
        if (canAnimate() && rafId === null) rafId = window.requestAnimationFrame(frame);
        if (!canAnimate()) stop();
      };

      const observer = new IntersectionObserver(([entry]) => {
        intersecting = entry.isIntersecting && entry.intersectionRatio >= 0.25;
        setFixedUiSafeState();
        playIfAllowed();
      }, { threshold: [0, 0.25, 1] });
      observer.observe(hero);

      const resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(visual);
      const onVisibilityChange = () => playIfAllowed();
      const onMotionChange = () => playIfAllowed();
      const onResize = () => { setFixedUiSafeState(); resize(); };
      const onPointerMove = (event) => {
        if (compactQuery.matches || motionQuery.matches || revealElapsed < LIQUID_GLASS_REVEAL_MS) return;
        const bounds = visual.getBoundingClientRect();
        pointer.targetX = Math.max(-1, Math.min(1, ((event.clientX - bounds.left) / bounds.width - 0.5) * 2));
        pointer.targetY = Math.max(-1, Math.min(1, ((event.clientY - bounds.top) / bounds.height - 0.5) * 2));
        if (pointerFrame !== null) return;
        pointerFrame = window.requestAnimationFrame(() => {
          pointerFrame = null;
          pointer.x += (pointer.targetX - pointer.x) * 0.16;
          pointer.y += (pointer.targetY - pointer.y) * 0.16;
          render();
        });
      };
      const onContextLost = (event) => { event.preventDefault(); stop(); setFallback(); };
      const onContextRestored = () => { setFallback(); };

      document.addEventListener("visibilitychange", onVisibilityChange);
      motionQuery.addEventListener("change", onMotionChange);
      compactQuery.addEventListener("change", onResize);
      window.addEventListener("resize", onResize);
      visual.addEventListener("pointermove", onPointerMove);
      canvas.addEventListener("webglcontextlost", onContextLost, false);
      canvas.addEventListener("webglcontextrestored", onContextRestored, false);
      resize();
      if (motionQuery.matches) playIfAllowed();

      return () => {
        disposed = true;
        stop();
        if (pointerFrame !== null) window.cancelAnimationFrame(pointerFrame);
        observer.disconnect();
        resizeObserver.disconnect();
        document.removeEventListener("visibilitychange", onVisibilityChange);
        motionQuery.removeEventListener("change", onMotionChange);
        compactQuery.removeEventListener("change", onResize);
        window.removeEventListener("resize", onResize);
        visual.removeEventListener("pointermove", onPointerMove);
        canvas.removeEventListener("webglcontextlost", onContextLost, false);
        canvas.removeEventListener("webglcontextrestored", onContextRestored, false);
        shell.geometry.dispose(); inner.geometry.dispose(); rim.geometry.dispose(); shadow.geometry.dispose();
        cyanCaustic.geometry.dispose(); limeCaustic.geometry.dispose(); coralCaustic.geometry.dispose();
        shellMaterial.dispose(); innerMaterial.dispose(); rimMaterial.dispose(); shadowMaterial.dispose();
        causticMaterials.forEach((material) => material.dispose());
        environment.environment.dispose(); environment.texture.dispose(); environment.generator.dispose();
        renderer.dispose();
        document.body.classList.remove("home-liquid-glass-safe-active");
      };
    } catch {
      setFallback();
      return undefined;
    }
  }, []);

  return (
    <section className="home-liquid-glass-hero" ref={heroRef} aria-labelledby="home-liquid-glass-heading">
      <style>{`
        .home-liquid-glass-hero { position: relative; overflow: hidden; min-height: 760px; padding: 162px 0 82px; background: var(--bg, #f7f6f1); color: var(--fg, #14181e); isolation: isolate; }
        .home-liquid-glass-hero *, .home-liquid-glass-hero *::before, .home-liquid-glass-hero *::after { box-sizing: border-box; }
        .home-liquid-glass-grid { display: grid; grid-template-columns: minmax(0, .91fr) minmax(420px, 1.09fr); gap: clamp(36px, 6vw, 94px); align-items: center; min-height: 516px; }
        .home-liquid-glass-copy { position: relative; z-index: 2; }
        .home-liquid-glass-eyebrow { display: flex; align-items: center; gap: 11px; max-width: 380px; margin: 0 0 27px; color: #46515e; font-family: var(--font-mono, "JetBrains Mono", monospace); font-size: 10px; font-weight: 600; letter-spacing: .105em; line-height: 1.45; text-transform: uppercase; }
        .home-liquid-glass-eyebrow::before { width: 24px; height: 1px; background: #2452bc; content: ""; flex: 0 0 auto; }
        .home-liquid-glass-title { max-width: 660px; margin: 0; font-family: var(--font-sans, Manrope, sans-serif); font-size: clamp(54px, 5.55vw, 86px); font-weight: 700; letter-spacing: -.058em; line-height: .98; text-wrap: balance; }
        .home-liquid-glass-title em { color: #174bb8; font-style: normal; }
        .home-liquid-glass-lead { max-width: 490px; margin: 30px 0 0; color: #4e5864; font-size: 18px; line-height: 1.55; }
        .home-liquid-glass-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 34px; }
        .home-liquid-glass-actions a { min-height: 50px; align-items: center; justify-content: center; }
        .home-liquid-glass-actions a:focus-visible { outline: 3px solid #70d8e9; outline-offset: 4px; }
        .home-liquid-glass-visual { position: relative; min-height: 516px; aspect-ratio: 1.12 / 1; cursor: default; }
        .home-liquid-glass-visual::before { position: absolute; inset: 7% 4% 4%; border: 1px solid rgba(33, 45, 66, .13); border-radius: 48% 52% 46% 54%; content: ""; pointer-events: none; }
        .home-liquid-glass-canvas { position: absolute; inset: 0; display: block; width: 100%; height: 100%; }
        .home-liquid-glass-fallback { position: absolute; inset: 9% 5% 8%; overflow: hidden; border-radius: 49% 51% 55% 45%; background: #d7edf0; box-shadow: 0 27px 48px rgba(38, 53, 81, .14); }
        .home-liquid-glass-fallback[hidden] { display: none; }
        .home-liquid-glass-fallback::before, .home-liquid-glass-fallback::after { position: absolute; border-radius: 50%; content: ""; }
        .home-liquid-glass-fallback::before { width: 76%; height: 76%; top: 8%; left: 6%; background: #2b67bf; box-shadow: 82px 76px 0 #76d9e6, 42px 142px 0 #e07071; }
        .home-liquid-glass-fallback::after { width: 27%; height: 27%; top: 18%; right: 10%; background: #d9e64d; box-shadow: -36px 156px 0 #c53c76; }
        .home-liquid-glass-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
        @media (min-width: 721px) and (max-width: 1020px) { body.home-liquid-glass-safe-active .nx-ai-launcher { opacity: 0 !important; pointer-events: none !important; transform: translate3d(0, 12px, 0) !important; animation: none !important; } }
        @media (max-width: 1020px) {
          .home-liquid-glass-hero { min-height: 0; padding: 132px 0 72px; }
          .home-liquid-glass-grid { grid-template-columns: 1fr; gap: 30px; min-height: 0; }
          .home-liquid-glass-copy { max-width: 650px; }
          .home-liquid-glass-title { font-size: clamp(58px, 8vw, 76px); }
          .home-liquid-glass-visual { width: min(560px, 100%); height: 540px; min-height: 0; margin-left: auto; aspect-ratio: auto; }
        }
        @media (max-width: 720px) {
          body.home-liquid-glass-safe-active .mobile-app-nav { opacity: 0 !important; pointer-events: none !important; transform: translateY(calc(96px + env(safe-area-inset-bottom))) scale(.96) !important; }
          body.home-liquid-glass-safe-active .mobile-ai-hint { opacity: 0 !important; pointer-events: none !important; transform: translate(-50%, 8px) scale(.96) !important; }
          .home-liquid-glass-hero { min-height: 0; padding: 118px 0 94px; }
          .home-liquid-glass-grid { display: block; min-height: 0; }
          .home-liquid-glass-eyebrow { max-width: 305px; margin-bottom: 23px; font-size: 9px; }
          .home-liquid-glass-title { max-width: 320px; font-size: clamp(42px, 12vw, 50px); letter-spacing: -.053em; line-height: .99; }
          .home-liquid-glass-lead { max-width: 312px; margin-top: 23px; font-size: 15.5px; line-height: 1.52; }
          .home-liquid-glass-actions { display: grid; width: min(100%, 276px); margin-top: 27px; }
          .home-liquid-glass-actions a { width: 100%; min-height: 52px; }
          .home-liquid-glass-visual { width: min(100%, 350px); height: 300px; min-height: 0; margin: 27px auto 0; aspect-ratio: auto; }
          .home-liquid-glass-visual::before { inset: 5% 2% 2%; }
          .home-liquid-glass-fallback { inset: 7% 4% 5%; }
        }
        @media (prefers-reduced-motion: reduce) { .home-liquid-glass-hero *, .home-liquid-glass-hero *::before, .home-liquid-glass-hero *::after { transition-duration: .01ms !important; } }
      `}</style>
      <div className="container home-liquid-glass-grid">
        <div className="home-liquid-glass-copy">
          <p className="home-liquid-glass-eyebrow">AI-DRIVEN BUSINESS &amp; PRODUCT DEVELOPMENT · MYANMAR</p>
          <h1 className="home-liquid-glass-title" id="home-liquid-glass-heading">Turn business questions into <em>working change.</em></h1>
          <p className="home-liquid-glass-lead">AI supports research, design, and delivery. People remain responsible for important decisions.</p>
          <div className="home-liquid-glass-actions">
            <a href={S.nextStep} onClick={startConsultation} className="btn btn-primary btn-arrow"><span>Start a consultation</span></a>
            <a href={S.services} className="btn btn-secondary btn-arrow"><span>Explore solutions</span></a>
          </div>
        </div>
        <div className="home-liquid-glass-visual" ref={visualRef} aria-hidden="true">
          <canvas className="home-liquid-glass-canvas" ref={canvasRef} />
          <div className="home-liquid-glass-fallback" ref={fallbackRef} hidden />
        </div>
        <p className="home-liquid-glass-sr">An abstract liquid glass sculpture represents connected work across AI-supported research, delivery, and human responsibility.</p>
      </div>
    </section>
  );
}

function HomeLegacyHero() {
  return (
    <section
      style={{ position: "relative", overflow: "hidden" }}
      className="grain hero-section hero-h8-review"
    >
      <style>{`
        @media (min-width: 961px) and (max-height: 800px) {
          .hero-h8-review { padding-top: 132px; padding-bottom: 64px; }
          .hero-h8-review .hero-meta-row { margin-bottom: 36px; }
          .hero-h8-review .hero-headline-clean { font-size: clamp(68px, 5.5vw, 78px); line-height: .96; }
          .hero-h8-review .hero-lead { margin-top: 20px !important; font-size: 17px !important; }
          .hero-h8-review .hero-h8-trust { margin-top: 12px !important; }
          .hero-h8-review .hero-actions { margin-top: 24px !important; }
        }
      `}</style>
      <div
        aria-hidden="true"
        style={{
          position: "absolute", top: -200, left: "50%", transform: "translateX(-50%)",
          width: 1400, height: 800,
          background: "radial-gradient(ellipse at center, oklch(80% 0.06 270 / 0.30) 0%, transparent 60%)",
          pointerEvents: "none",
        }}
      />

      <div className="container" style={{ position: "relative" }}>
        <div className="hero-meta-row">
          <div className="eyebrow">{"AI-DRIVEN BUSINESS & PRODUCT DEVELOPMENT"}</div>
        </div>

        <div
          className="hero-split"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.22fr) minmax(0, 1fr)",
            gap: 64,
            alignItems: "stretch",
          }}
        >
          <div>
            <h1 className="lift-in hero-headline-clean" style={{ animationDelay: "60ms" }}>
              <span>Turn business questions</span> into practical change.
            </h1>
            <p className="lift-in hero-lead" style={{
              animationDelay: "180ms", marginTop: 28, fontSize: 18,
              color: "var(--fg-muted)", lineHeight: 1.55, maxWidth: 520,
            }}>
              NEXT INNOVATIONS is a Myanmar-based, AI-driven business and product development company. We work with Myanmar, Japanese, and international teams to improve customer experiences, workflows, products, systems, and team operations.
            </p>
            <p className="lift-in hero-h8-trust" style={{ animationDelay: "220ms", marginTop: 16, fontSize: 14, color: "var(--fg-muted)", lineHeight: 1.55, maxWidth: 520 }}>
              AI supports research, design, and delivery. People remain responsible for important decisions.
            </p>
            <div className="flex gap-3 lift-in hero-actions" style={{ animationDelay: "260ms", marginTop: 32 }}>
              <a href={S.nextStep} onClick={startConsultation} className="btn btn-primary btn-arrow"><span>Start a consultation</span></a>
              <a href={S.services} className="btn btn-secondary btn-arrow"><span>Explore solutions</span></a>
            </div>
          </div>

          <div className="lift-in" style={{ animationDelay: "320ms", height: "100%", display: "flex" }}>
            <HeroThree />
          </div>
        </div>
      </div>
    </section>
  );
}

function Hero() { return <HomeLegacyHero />; }

export { Hero };
